import React, { useEffect, useState, useRef } from 'react'

import clsx from 'clsx'

import styles from './style.module.scss'
import { FormProvider, useForm, UseFormMethods } from 'react-hook-form'
import XText from 'v2/components/XText'
import ConditionalRender from 'utils/conditionalRender'
import XButton, { ButtonType } from 'v2/components/XButton'
import XColors from '../../styles/colors'
import XIcon from 'v2/components/XIcon'
import XInputError from 'v2/components/XInputError'
import { BUTTON_SIZE, BUTTON_STYLE_TYPE, XTEXT_ALIGNMENT, XTEXT_SIZE } from 'v2/styles/constants'
import { desktopThreshold } from 'components/BaseComponents/constants'
import { useTranslation } from 'i18n'

export interface XCardProps {
    children?: any
    type?: string
    title?: string
    titleSize?: string
    titleColor?: string
    description?: any[]
    descriptionSize?: string
    descriptionColor?: string
    backButton?: string
    backButtonAction?: () => void
    continueButton?: string
    continueAction?: () => void
    cancelButton?: string
    cancelAction?: () => void
    linkButton?: string
    linkAction?: () => void
    disclaimer?: any
    form?: UseFormMethods
    icon?: any
    loading?: boolean
    error?: string
    bottomContent?: any
    edit?: boolean
    captchaValidation?: boolean
}

export const XCARD_TYPE = {
    SINGLE: 'card__single',
    ERROR: 'card__multiple',
    EDIT: 'card__edit',
    EMAIL: 'card__support_email',
    MODAL: 'card__modal',
    MODAL_ADDRESS: 'card__modal__address',
    HOME: 'card__home',
}

const XCard: React.FC<XCardProps> = (props: XCardProps) => {
    const rootRef = useRef<HTMLDivElement>(null)
    const form = props.form || useForm()

    const { t } = useTranslation()

    const [isMobile, setIsMobile] = useState<boolean>(false)

    const onResize = () => {
        setIsMobile(window.innerWidth < desktopThreshold)
    }

    useEffect(() => {
        onResize()
        window.removeEventListener('resize', onResize)
        window.addEventListener('resize', onResize)
    }, [])

    const {
        type = XCARD_TYPE.SINGLE,
        loading,
        children,
        title,
        titleSize,
        titleColor,
        description,
        descriptionSize,
        descriptionColor,
        continueButton,
        continueAction,
        linkButton,
        linkAction,
        bottomContent,
        backButton,
        backButtonAction,
        cancelButton,
        cancelAction,
        captchaValidation,
        icon,
        error,
    } = props

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            form.handleSubmit(continueAction)()
        }
    }

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    return (
        <div className={clsx(styles.card, styles[type || XCARD_TYPE.SINGLE])}>
            <FormProvider {...form}>
                <ConditionalRender if={backButton && (type !== XCARD_TYPE.EDIT || isMobile)}>
                    <div className={styles.back__container}>
                        <XButton
                            styleType={BUTTON_STYLE_TYPE.ICON}
                            size={BUTTON_SIZE.SMALL}
                            leftIcon={'arrow_back'}
                            onClick={backButtonAction}
                            ga4_key={t('back', { lang: 'en' })}
                        >
                            {backButton}
                        </XButton>

                        <div className={styles.back__separator} />
                    </div>
                </ConditionalRender>
                <ConditionalRender if={icon}>
                    <div className={styles.icon__container}>
                        <XIcon icon={icon} size={isMobile ? 31 : 63} tintColor={XColors.primaryColor} />
                    </div>
                </ConditionalRender>
                <div className={!backButton ? styles.no_back_margin : null}>
                    <XText
                        size={titleSize ? titleSize : XTEXT_SIZE.EXTRA_LARGE}
                        color={titleColor ? titleColor : XColors.backgroundColor}
                        alignment={
                            XCARD_TYPE.EMAIL === props.type && !isMobile ? XTEXT_ALIGNMENT.LEFT : XTEXT_ALIGNMENT.CENTER
                        }
                    >
                        {title}
                    </XText>
                </div>

                <ConditionalRender if={description}>
                    <div className={styles.description__container}>
                        {description?.map((paragraph, index) => {
                            return (
                                <XText
                                    key={index}
                                    size={descriptionSize ? descriptionSize : XTEXT_SIZE.MEDIUM}
                                    color={descriptionColor ? descriptionColor : XColors.darkGray}
                                    alignment={XTEXT_ALIGNMENT.CENTER}
                                >
                                    {paragraph}
                                </XText>
                            )
                        })}
                    </div>
                </ConditionalRender>
                {children}
                <ConditionalRender if={continueButton || cancelButton}>
                    <div className={styles.continue__container} id={'continueButton'}>
                        <ConditionalRender if={cancelButton}>
                            <XButton
                                type={ButtonType.SUBMIT}
                                styleType={BUTTON_STYLE_TYPE.CANCEL}
                                onClick={cancelAction}
                                loading={loading}
                                ga4_key={t('cancel', { lang: 'en' })}
                            >
                                {cancelButton}
                            </XButton>
                        </ConditionalRender>

                        <ConditionalRender if={continueButton}>
                            <XButton
                                type={ButtonType.SUBMIT}
                                styleType={
                                    captchaValidation || (loading && XCARD_TYPE.EMAIL === props.type)
                                        ? BUTTON_STYLE_TYPE.DISABLED_PRIMARY
                                        : BUTTON_STYLE_TYPE.PRIMARY
                                }
                                onClick={continueAction}
                                loading={loading}
                                disabled={captchaValidation}
                                ga4_key={t('continue', { lang: 'en' })}
                            >
                                {continueButton}
                            </XButton>
                        </ConditionalRender>
                    </div>
                </ConditionalRender>
                <ConditionalRender if={props.disclaimer}>
                    <div className={styles.gray_container}>{props.disclaimer}</div>
                </ConditionalRender>
                <ConditionalRender if={linkButton}>
                    <XButton styleType={BUTTON_STYLE_TYPE.PRIMARY} onClick={linkAction}>
                        {linkButton}
                    </XButton>
                </ConditionalRender>
                {bottomContent}

                <ConditionalRender if={error}>
                    <XInputError id={'continueButton-error'} forElementId={'continueButton'}>
                        {error}
                    </XInputError>
                </ConditionalRender>
            </FormProvider>
        </div>
    )
}

XCard.displayName = 'XCard'

export default XCard
